/**
 * Integration Tracking Functions
 * Helper functions for tracking integration-related events in Heap Analytics
 *
 * Architecture Note:
 * These functions do NOT check if Heap is available before calling trackEvent.
 * This is intentional - the underlying trackEvent function in heapAnalytics.js
 * handles all availability checks, error handling, and graceful degradation.
 * See taskTracking.js for detailed explanation of this architectural decision.
 */

import { trackEvent } from './heapAnalytics';
import { AnalyticsEventNames } from './constants';

/**
 * Track when a merchant adds an integration
 * @param {Object} integrationData - Integration data
 * @param {string} integrationData.integrationId - Integration ID
 * @param {string} integrationData.integrationType - Type of integration (e.g., 'quickbooks', 'xero')
 * @param {string} integrationData.appName - Name of the integration app
 * @param {boolean} integrationData.isFirstIntegration - Whether this is the merchant's first integration
 */
export const trackIntegrationAdded = integrationData => {
  try {
    const eventData = {
      integrationId: integrationData.integrationId || '',
      integrationType: integrationData.integrationType || '',
      appName: integrationData.appName || '',
      isFirstIntegration: integrationData.isFirstIntegration ?? false,
    };

    // If this is the first integration, track the special first_integration_added event
    // Otherwise, track the general integration_added event
    if (integrationData.isFirstIntegration) {
      trackEvent(AnalyticsEventNames.FIRST_INTEGRATION_ADDED, eventData);
    } else {
      trackEvent(AnalyticsEventNames.INTEGRATION_ADDED, eventData);
    }
  } catch (error) {
    console.error('[Heap] Error tracking integration added:', error);
  }
};

/**
 * Track when a merchant successfully connects an integration with their accounting software
 * @param {Object} integrationData - Integration data
 * @param {string} integrationData.integrationId - Integration ID
 * @param {string} integrationData.integrationType - Type of integration (e.g., 'quickbooks', 'xero')
 * @param {string} integrationData.appName - Name of the integration app
 * @param {boolean} integrationData.isFirstIntegration - Whether this is the merchant's first integration
 */
export const trackIntegrationConnected = integrationData => {
  try {
    const eventData = {
      integrationId: integrationData.integrationId || '',
      integrationType: integrationData.integrationType || '',
      appName: integrationData.appName || '',
      isFirstIntegration: integrationData.isFirstIntegration ?? false,
    };

    // If this is the first integration, track the special first_integration_connected event
    // Otherwise, track the general integration_connected event
    if (integrationData.isFirstIntegration) {
      trackEvent(AnalyticsEventNames.FIRST_INTEGRATION_CONNECTED, eventData);
    } else {
      trackEvent(AnalyticsEventNames.INTEGRATION_CONNECTED, eventData);
    }
  } catch (error) {
    console.error('[Heap] Error tracking integration connected:', error);
  }
};

/**
 * Track when a merchant disconnects an integration
 * @param {Object} integrationData - Integration data
 * @param {string} integrationData.integrationId - Integration ID
 * @param {string} integrationData.integrationType - Type of integration (e.g., 'quickbooks', 'xero')
 * @param {string} integrationData.appName - Name of the integration app
 * @param {boolean} integrationData.hasOtherConnectedIntegrations - Whether there are other connected integrations
 */
export const trackIntegrationDisconnected = integrationData => {
  try {
    const eventData = {
      integrationId: integrationData.integrationId || '',
      integrationType: integrationData.integrationType || '',
      appName: integrationData.appName || '',
      hasOtherConnectedIntegrations:
        integrationData.hasOtherConnectedIntegrations ?? false,
    };

    trackEvent(AnalyticsEventNames.INTEGRATION_DISCONNECTED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking integration disconnected:', error);
  }
};

/**
 * Track when a new user is added to the system
 * @param {Object} userData - User data
 * @param {string} userData.userEmail - User's email address
 * @param {string} userData.firstName - User's first name
 * @param {string} userData.lastName - User's last name
 * @param {string} userData.roleId - Role ID assigned to user
 * @param {string} userData.privilegeId - Privilege ID assigned to user
 */
export const trackUserAdded = userData => {
  try {
    const eventData = {
      userEmail: userData.userEmail || '',
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
      roleId: userData.roleId || '',
      privilegeId: userData.privilegeId || '',
    };

    trackEvent(AnalyticsEventNames.NEW_USER_CREATED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking user added:', error);
  }
};
